/**
 * Persisted tenant/restaurant selection lives in localStorage under namespaced
 * keys (e.g. `management-app_selected_tenant_id`) managed by SelectionContext.
 * This pure helper clears ALL of it regardless of namespace.
 *
 * Kept dependency-free (no React / Firebase) so it can be called from the
 * sign-out mutation and admin guard without pulling the SelectionContext module
 * graph into those code paths.
 */

/** Substrings that identify any persisted selection key, across all namespaces. */
const SELECTION_KEY_FRAGMENTS = ['selected_tenant', 'selected_restaurant'] as const;

/**
 * Removes ALL persisted tenant/restaurant selection state from localStorage,
 * regardless of the `SelectionProvider` namespace. Call on sign-out so a
 * selection never leaks into the next user's session on a shared browser — the
 * full-page onboarding→management redirect and cross-app sign-out both bypass
 * the in-context `clearSelection()`. Mirrors the legacy inline sweep that lived
 * in AdminGuard. No-op when localStorage is unavailable (SSR / privacy mode).
 */
export function clearSelectionStorage(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (key && SELECTION_KEY_FRAGMENTS.some(fragment => key.includes(fragment))) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => window.localStorage.removeItem(key));
    } catch {
        // Silently ignore — localStorage may be unavailable or throw under
        // privacy settings; clearing selection is best-effort hygiene.
    }
}
